import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { groupWith } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'

import { Box, TableContainer, alpha } from '@material-ui/core'

import { createCollectForPrint } from '@/services/collects'
import { formatter } from '@/services/formatter'
import theme from '@/theme'

// import { ChipStatus } from '@/components/ChipStatus'
import { TableBody, TableCell, TableHead, TableRow } from '@/components/Table'
import { Table } from '@/components/Table/styles'
import { TablePagination } from '@/components/TablePagination'
import { usePagination } from '@/hooks/usePagination'
import { issuedsRequest } from '@/store/modules/users/collections/reducer'

import { TableSpinner } from '@/components/Table'
import { TableNoData } from '@/components/Table/TableNoData'
import { IssuedCollectsTableHeader } from './Header'
import { CollectObservation } from './Status'

export function IssuedCollectsTable({
  onPrintCollect,
  requestFilters,
  collectStatus,
}) {
  const count = useSelector((state) => state.users.collections.issueds.count)
  const data = useSelector((state) => state.users.collections.issueds.issueds)
  const loading = useSelector((state) => state.users.collections.loading)

  const dispatch = useDispatch()

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage, reset } =
    usePagination({
      initialRowsPerpage: 10,
    })

  useEffect(() => {
    const filters = requestFilters.reduce((acc, item) => {
      const itemValue = item.value?.trim()

      if (acc[item.filterBy]) {
        acc[item.filterBy].push(itemValue)
      } else {
        acc[item.filterBy] = [itemValue]
      }

      return acc
    }, {})

    const queryParameters = {
      status: collectStatus,
      page,
      per_page: rowsPerPage,
      filters,
    }

    dispatch(issuedsRequest(queryParameters))
  }, [dispatch, page, rowsPerPage, requestFilters, collectStatus])

  useEffect(() => {
    reset()
  }, [reset, collectStatus])

  function handlePrint(collectId) {
    onPrintCollect(createCollectForPrint(collectId, data))
  }

  // const renderShippingOrder = useCallback((coletaItem) => {
  //   if (!coletaItem.ford_numero) return null

  //   if (coletaItem.vpt_data_cancelamento) {
  //     return (
  //       <Tooltip
  //         arrow
  //         title={coletaItem.vpt_motivo_cancelamento}
  //         placement="top-start"
  //       >
  //         <div>
  //           <ChipStatus
  //             size="small"
  //             label={coletaItem.ford_numero}
  //             background={theme.palette.error.main}
  //           />
  //         </div>
  //       </Tooltip>
  //     )
  //   }

  //   return (
  //     <ChipStatus
  //       size="small"
  //       label={coletaItem.ford_numero}
  //       background={theme.palette.info.main}
  //     />
  //   )
  // }, [])

  const getCellStyle = (coletaItem) => {
    if (coletaItem.vpt_data_cancelamento) {
      return {
        background: alpha(theme.palette.error.main, 0.05),
      }
    }
  }

  const coletas = useMemo(() => {
    /**
     * @type {unknown[][]}
     */
    const coletas = groupWith(groupById, data)

    return coletas.map((coletaItems) => ({
      header: coletaItems[0],
      items: coletaItems,
    }))
  }, [data])

  const noData = coletas.length === 0

  return (
    <Box>
      {loading && <TableSpinner />}

      {coletas.map((coleta) => (
        <TableContainer key={String(coleta.header.id)}>
          <Table size="medium">
            <TableBody>
              <TableRow>
                <TableCell colSpan={12}>
                  <IssuedCollectsTableHeader
                    data={coleta}
                    onPrint={handlePrint}
                  />
                </TableCell>
              </TableRow>
            </TableBody>

            <TableHead style={{ display: 'table-row-group' }}>
              <TableRow>
                <TableCell width="5%">PEDIDO</TableCell>
                <TableCell width="20%">CLIENTE</TableCell>
                <TableCell width="5%">PED.CLIENTE</TableCell>
                <TableCell width="10%">CIDADE/UF</TableCell>
                <TableCell width="20%">PRODUTO</TableCell>

                <TableCell width="5%">LOTE</TableCell>
                <TableCell width="5%" align="right">
                  QUANTIDADE
                </TableCell>
                <TableCell width="5%" align="right">
                  PESO
                </TableCell>
                <TableCell width="5%" align="right">
                  PAL.
                </TableCell>
                <TableCell width="5%" align="right">
                  CAIXAS
                </TableCell>
                <TableCell width="15%">REPRESENTANTE</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {coleta.items.map((item) => {
                return (
                  <React.Fragment key={String(item.id)}>
                    <TableRow style={getCellStyle(item)}>
                      <TableCell>{item.cod_pedido}</TableCell>
                      <TableCell>{item.cliente_nome}</TableCell>
                      <TableCell>{item.ped_seupedido || '-'}</TableCell>
                      <TableCell>
                        {item.cidade_cliente}/{item.dsc_uf}
                      </TableCell>
                      <TableCell>{item.dsc_abreviado}</TableCell>
                      <TableCell>{item.itped_lote}</TableCell>

                      <TableCell align="right">
                        {formatter(item.vpt_qtd).toDecimal()}
                      </TableCell>

                      <TableCell align="right">
                        {formatter(item.vpt_peso_bruto).toDecimal()}
                      </TableCell>

                      <TableCell align="right">{item.vpt_pallet}</TableCell>
                      <TableCell align="right">{item.vpt_qtd_caixas}</TableCell>
                      <TableCell>{item.rep_nome}</TableCell>
                      {/* <TableCell>{renderShippingOrder(item)}</TableCell> */}
                    </TableRow>

                    {item.vpt_obs && (
                      <TableRow>
                        <TableCell colSpan="11">
                          <CollectObservation content={item.vpt_obs} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      {noData && (
        <Table size="medium">
          <TableBody>
            <TableNoData
              emptyText="Nenhuma coleta encontrada"
              emptyTextSecondary="Tente ajustar seus filtros ou aguarde novos agendamentos."
              empty={noData}
              showEmptyContent={!loading}
            />
          </TableBody>
        </Table>
      )}

      <TablePagination
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </Box>
  )
}

IssuedCollectsTable.propTypes = {
  onPrintCollect: PropTypes.func,
}

function groupById(a, b) {
  return a.id_coleta_for_order === b.id_coleta_for_order
}
